import { hasCatalogLensSelection } from './scenarioLensCatalog.helpers';
import type { LensCatalogData } from './scenarioLensCatalog.types';
import {
    normalizeLensDisplayOrder,
    reorderLensesSelectedFirst,
    sortLensesByDisplayOrder,
} from './scenarioLensOrder.helpers';
import type {
    AppendCustomLensOptions,
    RemoveCustomLensOptions,
    ScenarioViewData,
    ScenarioViewLens,
} from './scenarioLens.types';

export {
    buildScenarioViewSeedFromLiveConfig,
    cloneScenarioView,
    mapPpaScenarioViewToScenarioViewData,
    mapPpaViewMetadataToScenarioViewData,
    normalizeScenarioView,
} from './scenarioLensViewMapping.helpers';

export {
    isPpaScenarioViewMetadataDegraded,
    isPpaScenarioViewMetadataUnusable,
} from './scenarioLensMetadata.helpers';

export {
    computeViewHasLocalEdits,
    diagnoseViewDirtyState,
    getActiveLensIdFromLenses,
    hasActiveLensChanged,
    hasSettingsPanelDraftChanges,
    VIEW_DIRTY_REASON_LABELS,
} from './scenarioLensDirty.helpers';

export type { ViewDirtyDiagnostics, ViewDirtyReason } from './scenarioLensDirty.helpers';

export type {
    AppendCustomLensOptions,
    RemoveCustomLensOptions,
    ScenarioViewSeedCreated,
    ViewDirtyStateParams,
} from './scenarioLens.types';

export {
    normalizeLensDisplayOrder,
    reorderLensOnCatalogSelectionChange,
    reorderLensesSelectedFirst,
    sortLensesByDisplayOrder,
    toggleLensSelection,
} from './scenarioLensOrder.helpers';

/** Keeps settings draft in sync when view gains lenses locally (e.g. new custom lens). */
export const mergeViewLensesIntoDraft = (
    view: ScenarioViewData,
    draftLenses: ScenarioViewLens[],
): ScenarioViewLens[] => {
    const viewLensById = new Map(view.lenses.map((lens) => [lens.lens_id, lens]));
    const draftIds = new Set(draftLenses.map((lens) => lens.lens_id));
    const missingFromDraft = view.lenses.filter((lens) => !draftIds.has(lens.lens_id));

    const syncedDraft = draftLenses.map((draftLens) => {
        const viewLens = viewLensById.get(draftLens.lens_id);
        if (!viewLens) {
            return draftLens;
        }

        return {
            ...draftLens,
            lens_name: viewLens.lens_name,
            is_private: viewLens.is_private,
        };
    });

    if (missingFromDraft.length === 0) {
        return sortLensesByDisplayOrder(syncedDraft);
    }

    return sortLensesByDisplayOrder([...syncedDraft, ...missingFromDraft]);
};

export const getVisibleLenses = (view: ScenarioViewData): ScenarioViewLens[] =>
    sortLensesByDisplayOrder(view.lenses.filter((lens) => lens.is_selected));

export const getActiveLens = (view: ScenarioViewData): ScenarioViewLens | null => {
    const visibleLenses = getVisibleLenses(view);
    return visibleLenses.find((lens) => lens.is_active_lens) ?? visibleLenses[0] ?? null;
};

const pickFallbackActiveLensId = (visibleLenses: ScenarioViewLens[]): number | null => {
    if (!visibleLenses.length) {
        return null;
    }

    const defaultLens = visibleLenses.find((lens) => lens.is_default_lens);
    if (defaultLens) {
        return defaultLens.lens_id;
    }

    const firstSystemLens = sortLensesByDisplayOrder(
        visibleLenses.filter((lens) => !lens.is_private),
    )[0];
    if (firstSystemLens) {
        return firstSystemLens.lens_id;
    }

    return visibleLenses[0].lens_id;
};

/** Prefer the lens immediately to the left; if deleted was first, use the right neighbor. */
export const resolveLensIdAfterDelete = (
    orderedLenses: ScenarioViewLens[],
    deletedLensId: number,
): number | null => {
    const deletedIndex = orderedLenses.findIndex((lens) => lens.lens_id === deletedLensId);
    if (deletedIndex === -1) {
        return null;
    }

    const remainingLenses = orderedLenses.filter((lens) => lens.lens_id !== deletedLensId);
    if (!remainingLenses.length) {
        return null;
    }

    return deletedIndex === 0
        ? remainingLenses[0].lens_id
        : remainingLenses[deletedIndex - 1].lens_id;
};

export const resolveActiveLensIdAfterDelete = (
    selectedLensesBeforeDelete: ScenarioViewLens[],
    deletedLensId: number,
    allLensesBeforeDelete: ScenarioViewLens[],
): number | null => {
    const orderedSelected = sortLensesByDisplayOrder(
        selectedLensesBeforeDelete.length > 0
            ? selectedLensesBeforeDelete
            : allLensesBeforeDelete.filter((lens) => lens.is_selected),
    );
    const neighborId = resolveLensIdAfterDelete(orderedSelected, deletedLensId);
    if (neighborId != null) {
        return neighborId;
    }

    if (orderedSelected.length > 0) {
        const viewSelected = sortLensesByDisplayOrder(
            allLensesBeforeDelete.filter((lens) => lens.is_selected),
        );
        const viewNeighborId = resolveLensIdAfterDelete(viewSelected, deletedLensId);
        if (viewNeighborId != null) {
            return viewNeighborId;
        }
    }

    // Sole selected lens was deleted — fall back to default system lens (Price).
    const remainingAll = sortLensesByDisplayOrder(
        allLensesBeforeDelete.filter((lens) => lens.lens_id !== deletedLensId),
    );
    return pickFallbackActiveLensId(remainingAll) ?? remainingAll[0]?.lens_id ?? null;
};

const setActiveLens = (view: ScenarioViewData, lensId: number): ScenarioViewData => ({
    ...view,
    lenses: view.lenses.map((lens) => ({
        ...lens,
        is_active_lens: lens.lens_id === lensId,
    })),
});

const getNextCustomLensId = (lenses: ScenarioViewLens[]) =>
    lenses.reduce((maxId, lens) => Math.max(maxId, lens.lens_id), 0) + 1;

const ensureActiveLensIsVisible = (view: ScenarioViewData): ScenarioViewData => {
    const visibleLenses = getVisibleLenses(view);
    if (!visibleLenses.length) {
        return view;
    }

    const activeLens = getActiveLens(view);
    if (activeLens && visibleLenses.some((lens) => lens.lens_id === activeLens.lens_id)) {
        return view;
    }

    return setActiveLens(view, pickFallbackActiveLensId(visibleLenses) ?? visibleLenses[0].lens_id);
};

export const appendCustomLens = (
    view: ScenarioViewData,
    lensName: string,
    lensId?: number,
    options?: AppendCustomLensOptions,
): ScenarioViewData => {
    const selectLens = options?.selectLens ?? false;
    const nextLensId = lensId ?? getNextCustomLensId(view.lenses);
    const nextDisplayOrder = view.lenses.length + 1;
    const nextLens: ScenarioViewLens = {
        lens_id: nextLensId,
        lens_name: lensName,
        is_private: true,
        is_selected: selectLens,
        display_order: nextDisplayOrder,
        is_active_lens: false,
    };

    const nextLenses = normalizeLensDisplayOrder([...view.lenses, nextLens]);

    if (!selectLens) {
        return ensureActiveLensIsVisible({
            ...view,
            lenses: nextLenses,
        });
    }

    return setActiveLens(
        {
            ...view,
            lenses: nextLenses.map((lens) => ({
                ...lens,
                is_active_lens: lens.lens_id === nextLensId,
            })),
        },
        nextLensId,
    );
};

export const removeCustomLensFromView = (
    view: ScenarioViewData,
    lensId: number,
    options?: RemoveCustomLensOptions,
): ScenarioViewData => {
    const nextView = {
        ...view,
        lenses: normalizeLensDisplayOrder(view.lenses.filter((lens) => lens.lens_id !== lensId)),
    };

    if (options?.activeSuccessorLensId != null) {
        return setActiveLens(nextView, options.activeSuccessorLensId);
    }

    return ensureActiveLensIsVisible(nextView);
};

export const renameCustomLensInView = (
    view: ScenarioViewData,
    lensId: number,
    lensName: string,
): ScenarioViewData => ({
    ...view,
    lenses: view.lenses.map((lens) =>
        lens.lens_id === lensId ? { ...lens, lens_name: lensName } : lens,
    ),
});

export const applyLensDraftToView = (view: ScenarioViewData, draftLenses: ScenarioViewLens[]): ScenarioViewData =>
    ensureActiveLensIsVisible({
        ...view,
        lenses: normalizeLensDisplayOrder(draftLenses),
    });

export const countPrivateLenses = (lenses: ScenarioViewLens[]): number =>
    lenses.filter((lens) => lens.is_private).length;

export function deriveLensIsSelected(catalog: LensCatalogData): boolean {
    return hasCatalogLensSelection(catalog);
}

/** Updates lens tab visibility from catalog drafts / resolved catalogs (>=1 selected attribute). */
export const syncViewLensSelectionFromCatalogs = (
    view: ScenarioViewData,
    resolveLensIsSelectedForLens: (lensId: number) => boolean | null,
): ScenarioViewData =>
    ensureActiveLensIsVisible({
        ...view,
        lenses: reorderLensesSelectedFirst(
            view.lenses.map((lens) => {
                const resolvedIsSelected = resolveLensIsSelectedForLens(lens.lens_id);
                if (resolvedIsSelected == null) {
                    return lens;
                }

                return { ...lens, is_selected: resolvedIsSelected };
            }),
        ),
    });
